import { useFormikSubmit } from "@certn/api";
import { components } from "@certn/api/generated-schemas/client-portal";
import {
  BasicButton,
  DialogContent,
  DialogFooter,
  DialogHeader,
  PrimaryButton,
  Stack,
  showToast,
} from "@certn/cascadia-react";
import { Form, Formik, FormikErrors } from "formik";
import { useTranslation } from "react-i18next";

import { DisableWebhookField } from "./DisableWebhookField";
import { FailedToVerifyWebhookMessage } from "../form-components/FailedToVerifyWebhookMessage";
import { WebhookURLInput } from "../form-components/WebhookURLInput";

interface Props {
  webhook: Pick<
    components["schemas"]["PublicAPIWebhookList"],
    "id" | "webhook_url" | "is_active"
  >;
  onSuccess: () => void;
  close: () => void;
}

interface EditWebhookFormValues {
  webhook_url?: string;
  is_active: boolean;
}

interface Errors extends FormikErrors<EditWebhookFormValues> {
  verification?: string;
}

export function EditWebhookForm({ close, onSuccess, webhook }: Props) {
  const { t } = useTranslation();

  const { submit, isSubmitting } = useFormikSubmit(
    `/api/internal/client-portal/public-api/webhooks/${webhook.id}/update/`,
    {
      onSuccess: () => {
        onSuccess();
        showToast(t("Webhook updated successfully"), { type: "success" });
        close();
      },
    }
  );

  return (
    <Formik<EditWebhookFormValues>
      initialValues={{
        webhook_url: webhook.webhook_url,
        is_active: webhook.is_active,
      }}
      onSubmit={submit}
    >
      {(formProps) => {
        const errors: Errors = formProps.errors;
        return (
          <Form>
            <DialogHeader onClose={close}>{t("Edit webhook")}</DialogHeader>
            <DialogContent>
              <Stack gap="space-4">
                <FailedToVerifyWebhookMessage error={errors["verification"]} />
                <DisableWebhookField />
                <WebhookURLInput />
              </Stack>
            </DialogContent>
            <DialogFooter>
              <BasicButton
                onPress={close}
                testID="cancel"
                isDisabled={isSubmitting}
              >
                {t("Cancel")}
              </BasicButton>
              <PrimaryButton
                testID="save-changes"
                type="submit"
                isDisabled={isSubmitting}
              >
                {t("Save changes")}
              </PrimaryButton>
            </DialogFooter>
          </Form>
        );
      }}
    </Formik>
  );
}
